import React, { useEffect, useState } from "react";
import { api, downloadUrl } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Paperclip, FileText, RefreshCcw } from "lucide-react";

export default function AdminMessages() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contact-messages");
      setList(data);
    } catch { toast.error("Could not load messages"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const open = async (m) => {
    setSel(m);
    if (!m.read) {
      try { await api.put(`/admin/contact-messages/${m.id}/read`); } catch {}
      setList((prev) => prev.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
    }
  };

  const reasonLabel = (r) => (r === "resubmission" ? "Resubmission" : "Other");

  return (
    <div data-testid="admin-messages">
      <div className="flex items-center justify-between mb-3">
        <h1 className="font-serif text-3xl text-[#001F3F]">Messages</h1>
        <button onClick={load} className="text-xs uppercase tracking-wider text-[#2BA6A6] hover:text-[#001F3F] flex items-center gap-1" data-testid="messages-refresh"><RefreshCcw className="w-3 h-3" /> Refresh</button>
      </div>
      <p className="text-sm text-gray-500 mb-6">Contact form submissions, including author document resubmissions with attachments.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="border border-gray-200 bg-white max-h-[70vh] overflow-y-auto">
          {loading ? <div className="p-6 text-sm text-gray-500">Loading…</div>
            : list.length === 0 ? <div className="p-6 text-sm text-gray-500" data-testid="messages-empty">No messages yet.</div>
            : list.map((m) => (
              <button key={m.id} onClick={() => open(m)} className={`w-full text-left px-4 py-3 border-t border-gray-100 hover:bg-[#F5F7FA] flex items-start gap-3 ${sel?.id === m.id ? "bg-[#F5F7FA]" : ""}`} data-testid={`message-${m.id}`}>
                <Mail className={`w-4 h-4 mt-1 shrink-0 ${m.read ? "text-gray-300" : "text-[#2BA6A6]"}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className={`text-sm truncate flex-1 ${m.read ? "text-gray-600" : "text-[#001F3F] font-semibold"}`}>{m.manuscript_title || m.subject || "(no subject)"}</div>
                    <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 border shrink-0 ${m.reason === "resubmission" ? "bg-[#E8F2F4] text-[#12796f] border-[#2BA6A6]/40" : "bg-gray-50 text-gray-600 border-gray-200"}`}>{reasonLabel(m.reason)}</span>
                  </div>
                  <div className="text-xs text-gray-500 truncate">{m.name} · {m.email}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">{(m.created_at || "").slice(0, 19).replace("T", " ")}{m.files?.length ? ` · ${m.files.length} file(s)` : ""}</div>
                </div>
              </button>
            ))}
        </div>

        <div className="md:col-span-2 border border-gray-200 bg-white p-6 min-h-[300px]">
          {sel ? (
            <div data-testid="message-detail">
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Info label="From">{sel.name} &lt;{sel.email}&gt;</Info>
                <Info label="Reason">{reasonLabel(sel.reason)}</Info>
                <Info label="Manuscript title">{sel.manuscript_title || "—"}</Info>
                <Info label="Subject">{sel.subject || "—"}</Info>
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#2BA6A6] mb-1">Message</div>
              <pre className="bg-[#F5F7FA] border border-gray-200 p-4 text-sm font-sans whitespace-pre-wrap text-[#0A1118]">{sel.message}</pre>

              {sel.files?.length > 0 && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#2BA6A6] mb-2">Attachments</div>
                  <ul className="space-y-2">
                    {sel.files.map((f, i) => (
                      <li key={i}>
                        <a href={downloadUrl(f.stored_path, f.filename)} className="inline-flex items-center gap-2 text-sm text-[#001F3F] hover:text-[#2BA6A6] underline" data-testid={`message-file-${i}`}>
                          <Paperclip className="w-3.5 h-3.5 text-[#2BA6A6]" /> {f.filename}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-5">
                <a href={`mailto:${sel.email}?subject=${encodeURIComponent("Re: " + (sel.manuscript_title || sel.subject || ""))}`} className="inline-flex items-center gap-2 bg-[#2BA6A6] text-white px-4 py-2 text-xs uppercase tracking-wider hover:bg-[#001F3F]" data-testid="message-reply">
                  <Mail className="w-3.5 h-3.5" /> Reply by email
                </a>
              </div>
            </div>
          ) : <div className="text-center text-gray-500 py-16 text-sm">Select a message to view</div>}
        </div>
      </div>
    </div>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-sm text-[#001F3F] break-words">{children}</div>
    </div>
  );
}
